import React, { useState, useEffect, useCallback } from 'react';
import axios from './lib/axiosClient';

const C = { bg:'#1a1c28', card:'#252838', border:'#2e3150', text:'#e2e8f0', muted:'#94a3b8', accent:'#f97316', green:'#4ade80', red:'#f87171', blue:'#60a5fa', amber:'#fbbf24' };
const fmt = n => '$' + Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});

const STATUS_STYLE = {
  overdue:  { bg:'#3b0a0a', color:'#f87171', label:'Overdue' },
  due_soon: { bg:'#422006', color:'#fbbf24', label:'Due Soon' },
  ok:       { bg:'#14532d', color:'#4ade80', label:'OK' },
};


function EquipmentSearch({ items, value, onChange }) {
  const [search, setSearch] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const selected = items.find(i => i.id === value);
  const filtered = search
    ? items.filter(i => (i.displayName||i.name).toLowerCase().includes(search.toLowerCase()))
    : items;
  return (
    <div style={{position:'relative',marginBottom:12}}>
      <div onClick={()=>setOpen(o=>!o)} style={{background:'#252838',border:'1px solid #2a2d3e',borderRadius:6,padding:'8px 12px',color: selected?'#e2e8f0':'#555',fontSize:13,cursor:'pointer',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <span>{selected ? (selected.displayName||selected.name) : 'All items (category-wide)'}</span>
        <span style={{color:'#555',fontSize:10}}>▼</span>
      </div>
      {open && (
        <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:100,background:'#252838',border:'1px solid #2a2d3e',borderRadius:6,marginTop:2,boxShadow:'0 8px 24px rgba(0,0,0,0.5)',maxHeight:240,overflow:'hidden',display:'flex',flexDirection:'column'}}>
          <input autoFocus value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search equipment..."
            style={{background:'#181928',border:'none',borderBottom:'1px solid #2a2d3e',padding:'8px 12px',color:'#e2e8f0',fontSize:12,outline:'none'}} />
          <div style={{overflowY:'auto',maxHeight:190}}>
            <div onClick={()=>{onChange('');setOpen(false);setSearch('');}} style={{padding:'8px 12px',fontSize:12,color:'#555',cursor:'pointer',borderBottom:'1px solid #2e3150'}}>All items (category-wide)</div>
            {filtered.map(i=>(
              <div key={i.id} onClick={()=>{onChange(i.id);setOpen(false);setSearch('');}}
                style={{padding:'8px 12px',fontSize:12,color:i.id===value?'#f97316':'#e2e8f0',background:i.id===value?'rgba(249,115,22,0.1)':'transparent',cursor:'pointer',borderBottom:'1px solid #1a1c2e'}}>
                {i.displayName||i.name}
              </div>
            ))}
            {filtered.length===0 && <div style={{padding:'12px',color:'#555',fontSize:12,textAlign:'center'}}>No equipment found</div>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function MaintenanceSchedules() {
  const [tab, setTab] = useState('due');       // due | schedules | history
  const [items, setItems] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [dueItems, setDueItems] = useState([]);
  const [metrics, setMetrics] = useState({ overdue:0, due_soon:0, total:0 });
  const [loading, setLoading] = useState(true);

  // Schedule form
  const [showForm, setShowForm] = useState(false);
  const [editSched, setEditSched] = useState(null);
  const [form, setForm] = useState({ rental_item_id:'', name:'', interval_type:'hours', interval_value:'', estimated_duration_hours:'', priority:'default', checklist:[] });
  const [saving, setSaving] = useState(false);

  // Log service form
  const [logModal, setLogModal] = useState(null); // { schedule, item }
  const [logForm, setLogForm] = useState({ hour_meter_at:'', performed_by:'', notes:'' });
  const [logging, setLogging] = useState(false);

  // History
  const [historyItem, setHistoryItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [allHistory, setAllHistory] = useState([]);
  const [allHistoryLoading, setAllHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [historyCollapsed, setHistoryCollapsed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [schedRes, dueRes, itemsRes] = await Promise.all([
        axios.get('/api/rentals/maintenance-schedules'),
        axios.get('/api/rentals/maintenance-schedules/due'),
        axios.get('/api/rentals/products'),
      ]);
      setSchedules(schedRes.data.schedules || []);
      setDueItems(dueRes.data.items || []);
      setMetrics({ overdue: dueRes.data.overdue || 0, due_soon: dueRes.data.due_soon || 0, total: dueRes.data.total || 0 });
      // Flatten variants into individual units
      const prods = itemsRes.data.products || [];
      const units = [];
      prods.forEach(p => {
        (p.rental_product_variants || p.variants || []).forEach(v => {
          units.push({
            id: v.id,
            name: p.name + (v.serial_number ? ' · ' + v.serial_number : ''),
            product_name: p.name,
            serial_number: v.serial_number || '',
            unit_status: v.unit_status,
            hour_meter: v.hour_meter || 0,
            is_active: p.is_published !== false,
          });
        });
      });
      setItems(units);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => { setForm({ rental_item_id:'', name:'', interval_type:'hours', interval_value:'', estimated_duration_hours:'', priority:'default', checklist:[] }); setEditSched(null); setShowForm(false); };

  const saveSchedule = async () => {
    if (!form.name || !form.interval_value) return alert('Name and interval required');
    setSaving(true);
    try {
      if (editSched) {
        await axios.put('/api/rentals/maintenance-schedules/' + editSched.id, form);
      } else {
        await axios.post('/api/rentals/maintenance-schedules', form);
      }
      resetForm();
      load();
    } catch(e) { alert(e.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  const deleteSchedule = async (id) => {
    if (!window.confirm('Delete this maintenance schedule?')) return;
    await axios.delete('/api/rentals/maintenance-schedules/' + id);
    load();
  };

  const startEdit = (s) => {
    setEditSched(s);
    setForm({ variant_id: s.variant_id||'', rental_item_id: s.variant_id||s.rental_item_id||'', name: s.name, interval_type: s.interval_type, interval_value: s.interval_value, estimated_duration_hours: s.estimated_duration_hours||'', priority: s.priority, checklist: s.checklist||[] });
    setShowForm(true);
  };

  const openLog = (dueItem) => {
    const item = items.find(i => i.id === dueItem.item_id);
    setLogModal({ schedule_id: dueItem.schedule_id, schedule_name: dueItem.schedule_name, item_id: dueItem.item_id, item_name: dueItem.item_name });
    setLogForm({ hour_meter_at: item?.hour_meter || dueItem.hour_meter || '', performed_by:'', notes:'' });
  };

  const submitLog = async () => {
    if (!logModal) return;
    setLogging(true);
    try {
      await axios.post(`/api/rentals/maintenance-schedules/${logModal.schedule_id}/log`, {
        variant_id: logModal.item_id,
        hour_meter_at: Number(logForm.hour_meter_at) || 0,
        performed_by: logForm.performed_by,
        notes: logForm.notes,
      });
      setLogModal(null);
      load();
    } catch(e) { alert(e.response?.data?.error || 'Failed'); }
    setLogging(false);
  };

  const loadAllHistory = async () => {
    setAllHistoryLoading(true);
    try {
      const r = await axios.get('/api/rentals/maintenance-schedules/history/all');
      setAllHistory(r.data.logs || []);
    } catch { setAllHistory([]); }
    setAllHistoryLoading(false);
  };
  const loadHistory = async (itemId) => {
    setHistoryItem(itemId);
    setHistoryLoading(true);
    try {
      const r = await axios.get(`/api/rentals/maintenance-schedules/${itemId}/history`);
      setHistory(r.data.logs || []);
    } catch { setHistory([]); }
    setHistoryLoading(false);
  };

  const inp = { padding:'8px 11px', borderRadius:6, border:`1px solid ${C.border}`, background:'#1e2030', color:C.text, fontSize:13, outline:'none', width:'100%', boxSizing:'border-box' };
  const lbl = { fontSize:10, fontWeight:700, color:C.muted, letterSpacing:1, textTransform:'uppercase', marginBottom:5, display:'block' };
  const card = { background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:20, marginBottom:16 };

  if (loading) return <div style={{color:C.muted,padding:40,textAlign:'center'}}>Loading maintenance data...</div>;

  return (
    <div style={{maxWidth:960}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:700,color:C.text}}>Maintenance Schedules</div>
          <div style={{fontSize:13,color:C.muted}}>Preventive maintenance tracking for your equipment</div>
        </div>
        <button onClick={()=>{resetForm();setShowForm(true);}} style={{padding:'9px 18px',borderRadius:8,border:'none',background:C.accent,color:'#fff',fontWeight:700,cursor:'pointer',fontSize:13}}>+ New Schedule</button>
      </div>

      {/* Metrics */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
        {[
          { label:'Overdue', val:metrics.overdue, color:C.red, icon:'🔴' },
          { label:'Due Soon', val:metrics.due_soon, color:C.amber, icon:'🟡' },
          { label:'Schedules', val:schedules.length, color:C.green, icon:'🟢' },
        ].map(m=>(
          <div key={m.label} style={{...card,marginBottom:0,display:'flex',alignItems:'center',gap:14}}>
            <div style={{fontSize:24}}>{m.icon}</div>
            <div>
              <div style={{fontSize:22,fontWeight:800,color:m.color}}>{m.val}</div>
              <div style={{fontSize:11,color:C.muted}}>{m.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:0,borderBottom:`1px solid ${C.border}`,marginBottom:16}}>
        {[['due','Due Now'],['schedules','Schedules'],['history','Service History']].map(([k,l])=>(
          <div key={k} onClick={()=>{ setTab(k); if(k==='history') loadAllHistory(); }} style={{padding:'8px 18px',fontSize:12,fontWeight:600,cursor:'pointer',color:tab===k?C.accent:C.muted,borderBottom:tab===k?`2px solid ${C.accent}`:'2px solid transparent',marginBottom:-1}}>{l}</div>
        ))}
      </div>

      {/* ── DUE TAB ── */}
      {tab==='due' && (
        <div>
          {dueItems.length === 0 && <div style={{...card,textAlign:'center',color:C.muted,fontSize:13,padding:40}}>All equipment is up to date on maintenance.</div>}
          {dueItems.map(d => {
            const st = STATUS_STYLE[d.status] || STATUS_STYLE.ok;
            const isHours = d.interval_type === 'hours';
            const currentHours = d.hour_meter || 0;
            const nextDueHours = d.next_service_hours || (d.interval_type === 'hours' ? Number(d.interval_value) : 0);
            const intervalVal = Number(d.interval_value) || 0;
            const progress = isHours && nextDueHours > 0
              ? Math.min(100, Math.round((currentHours / nextDueHours) * 100))
              : 0;
            const hoursLeft = isHours ? nextDueHours - currentHours : null;
            return (
              <div key={`${d.item_id}_${d.schedule_id}`} style={{...card, marginBottom:12, padding:'16px 20px', border:`1px solid ${d.status==='overdue'?'#5a1a1a':d.status==='due_soon'?'#4a3a0a':'#2e3150'}`}}>
                <div style={{display:'flex',alignItems:'flex-start',gap:14}}>
                  <div style={{width:44,height:44,borderRadius:10,background:'#2e3150',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>🔧</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                      <div style={{fontWeight:700,fontSize:14,color:C.text}}>{d.item_name || 'Unknown Machine'}</div>
                      <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:4,background:st.bg,color:st.color}}>{st.label}</span>
                    </div>
                    <div style={{fontSize:12,color:C.muted,marginBottom:8}}>{d.schedule_name} · Every {d.interval_value} {isHours?'hrs':'days'}</div>
                    {isHours && nextDueHours > 0 && (
                      <div style={{marginBottom:8}}>
                        <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:C.muted,marginBottom:4}}>
                          <span>Current: <strong style={{color:C.text}}>{currentHours} hrs</strong></span>
                          <span>Due at: <strong style={{color:st.color}}>{nextDueHours} hrs</strong></span>
                          <span style={{color:hoursLeft<0?C.red:hoursLeft<50?C.amber:C.green}}>{hoursLeft<0?`${Math.abs(hoursLeft)} hrs overdue`:`${hoursLeft} hrs remaining`}</span>
                        </div>
                        <div style={{height:6,background:'#2e3150',borderRadius:3,overflow:'hidden'}}>
                          <div style={{height:'100%',width:`${progress}%`,background:progress>=100?C.red:progress>=80?C.amber:C.green,borderRadius:3,transition:'width 0.3s'}} />
                        </div>
                      </div>
                    )}
                    {!isHours && d.next_due_date && (
                      <div style={{fontSize:11,color:st.color,marginBottom:8}}>Due: {d.next_due_date}</div>
                    )}
                    {d.reason && <div style={{fontSize:11,color:st.color}}>{d.reason}</div>}
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:6,flexShrink:0}}>
                    <button onClick={()=>openLog(d)} style={{padding:'7px 16px',borderRadius:6,border:'none',background:C.accent,color:'#fff',fontWeight:700,fontSize:12,cursor:'pointer',whiteSpace:'nowrap'}}>Log Service</button>
                    <button onClick={()=>{loadHistory(d.item_id);setTab('history');}} style={{padding:'6px 12px',borderRadius:6,border:`1px solid ${C.border}`,background:'transparent',color:C.blue,fontSize:11,cursor:'pointer'}}>View History</button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── SCHEDULES TAB ── */}
      {tab==='schedules' && (
        <div>
          {schedules.length === 0 && <div style={{...card,textAlign:'center',color:C.muted,fontSize:13}}>No maintenance schedules yet. Create one to start tracking.</div>}
          {schedules.map(s => (
            <div key={s.id} style={{...card,display:'flex',alignItems:'center',gap:16,marginBottom:10}}>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:13,color:C.text}}>{s.name}</div>
                <div style={{fontSize:11,color:C.muted}}>
                  {s.variant ? (s.variant.product_name || '') + (s.variant.serial_number ? ' · ' + s.variant.serial_number : '') : 'All items'} · {s.interval_type === 'hours' ? `Every ${s.interval_value} hrs` : `Every ${s.interval_value} days`}
                  {s.estimated_duration_hours > 0 && ` · ~${s.estimated_duration_hours}h`}
                </div>
                <div style={{display:'flex',gap:6,marginTop:4}}>
                  <span style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:4,background:s.is_active?'#14532d':'#2e3150',color:s.is_active?C.green:C.muted}}>{s.is_active?'Active':'Inactive'}</span>
                  <span style={{fontSize:9,fontWeight:700,padding:'2px 6px',borderRadius:4,background:'#2e3150',color:C.muted}}>{s.priority}</span>
                </div>
              </div>
              <button onClick={()=>startEdit(s)} style={{padding:'5px 10px',borderRadius:6,border:`1px solid ${C.border}`,background:'transparent',color:C.blue,fontSize:11,cursor:'pointer'}}>Edit</button>
              <button onClick={()=>deleteSchedule(s.id)} style={{padding:'5px 10px',borderRadius:6,border:'1px solid #3b0a0a',background:'transparent',color:C.red,fontSize:11,cursor:'pointer'}}>Delete</button>
            </div>
          ))}
        </div>
      )}

      {/* ── HISTORY TAB ── */}
      {tab==='history' && (
        <div>
          <div style={{display:'flex',gap:10,marginBottom:14,alignItems:'center',flexWrap:'wrap'}}>
            <input value={historySearch} onChange={e=>setHistorySearch(e.target.value)}
              placeholder="Search machine, technician, or service..."
              style={{...inp,flex:1,minWidth:200}} />
            <span style={{fontSize:11,color:C.muted}}>{allHistory.length} total records</span>
          </div>
          {allHistoryLoading && <div style={{color:C.muted,padding:20,textAlign:'center'}}>Loading...</div>}
          {!allHistoryLoading && allHistory.length===0 && <div style={{...card,textAlign:'center',color:C.muted,fontSize:13,padding:40}}>No service history yet. Log a service to get started.</div>}
          {!allHistoryLoading && (() => {
            const q = historySearch.toLowerCase();
            const filtered = allHistory.filter(h =>
              !q || (h.machine_name||'').toLowerCase().includes(q) ||
              (h.rental_maintenance_schedules?.name||'').toLowerCase().includes(q) ||
              (h.performed_by||'').toLowerCase().includes(q) ||
              (h.notes||'').toLowerCase().includes(q)
            );
            const groups = {};
            filtered.forEach(h => {
              const key = h.machine_name || 'Unknown';
              if (!groups[key]) groups[key] = [];
              groups[key].push(h);
            });
            return Object.entries(groups).map(([machine, logs]) => (
              <div key={machine} style={{marginBottom:12}}>
                <div onClick={()=>setHistoryCollapsed(p=>({...p,[machine]:!p[machine]}))}
                  style={{display:'flex',alignItems:'center',gap:8,padding:'8px 14px',background:'rgba(255,255,255,0.03)',borderRadius:8,cursor:'pointer',marginBottom:4,border:'1px solid #2e3150'}}>
                  <span style={{fontSize:10,color:'#555'}}>{historyCollapsed[machine]?'▶':'▼'}</span>
                  <span style={{fontWeight:700,fontSize:13,color:C.text}}>{machine}</span>
                  <span style={{fontSize:11,color:C.muted}}>({logs.length} records)</span>
                </div>
                {!historyCollapsed[machine] && logs.map((h,i)=>(
                  <div key={h.id} style={{...card,marginBottom:4,padding:'12px 18px',borderLeft:'3px solid '+C.green,marginLeft:12}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                      <div style={{flex:1}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3}}>
                          <span style={{fontSize:10,fontWeight:700,color:C.green,fontFamily:'monospace'}}>#{logs.length-i}</span>
                          <span style={{fontWeight:700,fontSize:12,color:C.text}}>{h.rental_maintenance_schedules?.name||'Service Event'}</span>
                        </div>
                        <div style={{fontSize:11,color:C.muted}}>
                          {new Date(h.performed_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                          {h.performed_by && <span> · <strong style={{color:C.text}}>{h.performed_by}</strong></span>}
                          {h.hour_meter_at > 0 && <span> · <strong style={{color:C.text}}>{h.hour_meter_at} hrs</strong></span>}
                        </div>
                        {h.notes && <div style={{fontSize:11,color:C.text,background:'#1a1c2e',padding:'5px 8px',borderRadius:4,marginTop:4}}>{h.notes}</div>}
                      </div>
                      <div style={{textAlign:'right',flexShrink:0,marginLeft:12}}>
                        {h.next_due_hours>0 && <div style={{fontSize:10,color:C.amber}}>Next: {h.next_due_hours} hrs</div>}
                        {h.next_due_date && <div style={{fontSize:10,color:C.amber}}>Next: {h.next_due_date}</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ));
          })()}
        </div>
      )}

      {/* ── SCHEDULE FORM MODAL ── */}
      {showForm && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,width:'100%',maxWidth:480,padding:24}}>
            <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:16}}>{editSched?'Edit Schedule':'New Maintenance Schedule'}</div>
            <label style={lbl}>Equipment</label>
            <EquipmentSearch items={items} value={form.variant_id||''} onChange={v=>setForm(p=>({...p,variant_id:v,rental_item_id:null}))} />
            <label style={lbl}>Schedule Name *</label>
            <input style={{...inp,marginBottom:12}} value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} placeholder="e.g. 500-Hour Service, Monthly Inspection" />
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
              <div>
                <label style={lbl}>Interval Type</label>
                <select style={inp} value={form.interval_type} onChange={e=>setForm(p=>({...p,interval_type:e.target.value}))}>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                  <option value="usage">Usage (mm)</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Every X {form.interval_type} *</label>
                <input type="number" style={inp} value={form.interval_value} onChange={e=>setForm(p=>({...p,interval_value:e.target.value}))} placeholder="e.g. 500" />
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:16}}>
              <div>
                <label style={lbl}>Est. Duration (hours)</label>
                <input type="number" style={inp} value={form.estimated_duration_hours} onChange={e=>setForm(p=>({...p,estimated_duration_hours:e.target.value}))} placeholder="e.g. 2" />
              </div>
              <div>
                <label style={lbl}>Priority</label>
                <select style={inp} value={form.priority} onChange={e=>setForm(p=>({...p,priority:e.target.value}))}>
                  <option value="low">Low</option>
                  <option value="default">Default</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={saveSchedule} disabled={saving} style={{flex:1,padding:'10px',borderRadius:8,border:'none',background:C.accent,color:'#fff',fontWeight:700,cursor:'pointer',fontSize:13}}>{saving?'Saving...':editSched?'Update':'Create Schedule'}</button>
              <button onClick={resetForm} style={{padding:'10px 16px',borderRadius:8,border:`1px solid ${C.border}`,background:'transparent',color:C.muted,cursor:'pointer',fontSize:13}}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* ── LOG SERVICE MODAL ── */}
      {logModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,width:'100%',maxWidth:420,padding:24}}>
            <div style={{fontSize:16,fontWeight:700,color:C.text,marginBottom:4}}>Log Service</div>
            <div style={{fontSize:12,color:C.muted,marginBottom:16}}>{logModal.item_name} — {logModal.schedule_name}</div>
            <label style={lbl}>Hour Meter Reading</label>
            <input type="number" style={{...inp,marginBottom:12}} value={logForm.hour_meter_at} onChange={e=>setLogForm(p=>({...p,hour_meter_at:e.target.value}))} placeholder="Current hours" />
            <label style={lbl}>Performed By</label>
            <input style={{...inp,marginBottom:12}} value={logForm.performed_by} onChange={e=>setLogForm(p=>({...p,performed_by:e.target.value}))} placeholder="Technician name" />
            <label style={lbl}>Notes</label>
            <textarea style={{...inp,height:70,resize:'vertical',marginBottom:16}} value={logForm.notes} onChange={e=>setLogForm(p=>({...p,notes:e.target.value}))} placeholder="Work performed, parts replaced..." />
            <div style={{display:'flex',gap:10}}>
              <button onClick={submitLog} disabled={logging} style={{flex:1,padding:'10px',borderRadius:8,border:'none',background:C.green,color:'#1e2235',fontWeight:700,cursor:'pointer',fontSize:13}}>{logging?'Logging...':'Log Service & Reset Counter'}</button>
              <button onClick={()=>setLogModal(null)} style={{padding:'10px 16px',borderRadius:8,border:`1px solid ${C.border}`,background:'transparent',color:C.muted,cursor:'pointer',fontSize:13}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
